// InvoiceLearningTab — 請求書 学習機構 (= schema-v37/v38/v39, 2026-05-09)
//
// 5 sub-tab:
//   INV-1 (arica)   : ARICA 分類精度
//   INV-2 (cadence) : Partner cadence 学習度
//   INV-3 (curated) : partner-patterns.md 記録率
//   INV-4 (dedup)   : 重複検出発動回数
//   INV-7 (outcome) : 結果ログ + cadence prediction summary
//
// v39 (Codex F8 + F9):
//   F8: subTab prop で URL hash 永続化対応 (LearningPage 経由)
//   F9: 結果ログタブで Recommend 提案率 / 採用率 / 完全一致率 を 3 metric 明示分離

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  CardBase,
  Note,
  TabBar,
  MessageBlock,
  SubSectionTitle,
  ProgressBar,
  InlineSpinner,
} from '@freee_jp/vibes';
import {
  fetchInvoiceLearningMetrics,
  fetchInvoicePartnerCadences,
  fetchInvoiceQualityIssues,
  fetchDeferredInvoices,
  InvoiceLearningMetrics,
  PartnerCadenceItem,
  InvoiceQualityIssues,
} from '../../api/client';

const formatPct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`;

export type InvoiceSubTabKey = 'arica' | 'cadence' | 'curated' | 'dedup' | 'outcome';
const SUB_TAB_KEYS: InvoiceSubTabKey[] = ['arica', 'cadence', 'curated', 'dedup', 'outcome'];

export interface InvoiceLearningTabProps {
  subTab?: InvoiceSubTabKey;
  onChangeSubTab?: (key: InvoiceSubTabKey) => void;
}

export const InvoiceLearningTab: React.FC<InvoiceLearningTabProps> = ({ subTab, onChangeSubTab }) => {
  const tabIndex = subTab ? Math.max(0, SUB_TAB_KEYS.indexOf(subTab)) : 0;
  const setTab = (i: number) => {
    if (onChangeSubTab) onChangeSubTab(SUB_TAB_KEYS[i] || 'arica');
  };
  const tab = tabIndex;
  const [metrics, setMetrics] = useState<InvoiceLearningMetrics | null>(null);
  const [cadences, setCadences] = useState<PartnerCadenceItem[]>([]);
  const [issues, setIssues] = useState<InvoiceQualityIssues | null>(null);
  const [deferredOpenCount, setDeferredOpenCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [mRes, cRes, qRes, dRes] = await Promise.allSettled([
      fetchInvoiceLearningMetrics(30),
      fetchInvoicePartnerCadences('all'),
      fetchInvoiceQualityIssues(),
      fetchDeferredInvoices({ status: 'open', limit: 1 }),
    ]);
    const errs: string[] = [];
    if (mRes.status === 'fulfilled') setMetrics(mRes.value);
    else errs.push(`metrics: ${mRes.reason instanceof Error ? mRes.reason.message : String(mRes.reason)}`);
    if (cRes.status === 'fulfilled') setCadences(cRes.value.cadences || []);
    else errs.push(`cadences: ${cRes.reason instanceof Error ? cRes.reason.message : String(cRes.reason)}`);
    if (qRes.status === 'fulfilled') setIssues(qRes.value);
    else errs.push(`quality_issues: ${qRes.reason instanceof Error ? qRes.reason.message : String(qRes.reason)}`);
    if (dRes.status === 'fulfilled') setDeferredOpenCount(dRes.value.totals?.open_count ?? 0);
    else errs.push(`deferred: ${dRes.reason instanceof Error ? dRes.reason.message : String(dRes.reason)}`);
    if (errs.length) setError(errs.join(' / '));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const aricaRate = metrics?.arica.registered_rate;
  const cadenceTotal = metrics?.cadence.total || 0;
  const cadenceHigh = metrics?.cadence.high || 0;
  const curatedRate = useMemo(() => {
    if (!metrics?.curated_patterns.total_in_index) return 0;
    return (metrics.curated_patterns.trusted_in_index || 0) / metrics.curated_patterns.total_in_index;
  }, [metrics]);
  // F9: KPI 上部は 採用率(prefill_used_rate) を表示 (= 提案 N 件中、ユーザーが触らずそのまま登録)
  const prefillRate = metrics?.outcome_log.prefill_used_rate;

  return (
    <>
      <Note>請求書 (ARICA + 25日払い) 関連の 学習指標</Note>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

      {/* 5 KPI summary */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px 8px', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
          <KpiBlock
            label="ARICA 登録率 (30日)"
            value={aricaRate != null ? `${(aricaRate * 100).toFixed(0)}%` : '—'}
            sub={metrics ? `${metrics.arica.registered} / ${metrics.arica.total}` : ''}
            color={aricaRate != null && aricaRate >= 0.8 ? '#338650' : '#dd7a00'}
          />
          <KpiBlock
            label="Cadence 高信頼"
            value={cadenceTotal > 0 ? `${cadenceHigh} / ${cadenceTotal}` : '—'}
            sub={cadenceTotal > 0 ? `${((cadenceHigh / cadenceTotal) * 100).toFixed(0)}%` : ''}
            color="#7c5cff"
          />
          <KpiBlock
            label="Curated patterns"
            value={metrics ? `${metrics.curated_patterns.total_in_d1}` : '—'}
            sub={metrics ? `index: trusted ${metrics.curated_patterns.trusted_in_index} / total ${metrics.curated_patterns.total_in_index}` : ''}
            color="#338650"
          />
          <KpiBlock
            label="重複検出 (30日)"
            value={metrics ? `${metrics.duplicate_detection.fired_30d}` : '—'}
            sub={metrics ? `arica:${metrics.duplicate_detection.by_source.arica} / fb:${metrics.duplicate_detection.by_source.filebox}` : ''}
            color="#2e7cf6"
          />
          <KpiBlock
            label="Prefill 採用率 (30日)"
            value={prefillRate != null ? `${(prefillRate * 100).toFixed(0)}%` : '—'}
            sub={metrics ? `${metrics.outcome_log.prefill_used_count} / ${metrics.outcome_log.total}` : ''}
            color="#338650"
          />
          <KpiBlock
            label="未処理 件数"
            value={deferredOpenCount != null ? String(deferredOpenCount) : '—'}
            sub={deferredOpenCount != null && deferredOpenCount > 0 ? '請求書処理 page で確認' : '0 件'}
            color={deferredOpenCount != null && deferredOpenCount > 0 ? '#dd7a00' : '#338650'}
          />
          {loading && <InlineSpinner isLoading={true} />}
        </div>
      </CardBase>

      {/* 5 sub-tab */}
      <CardBase paddingSize="small" mb={0.5}>
        <TabBar
          tabs={['ARICA 分類', 'Cadence 学習', 'Curated patterns', '重複検出', '結果ログ (P1)']}
          currentTabIndex={tab}
          onClickTab={(i) => setTab(i)}
          small
        />
      </CardBase>

      {/* TAB 0: INV-1 ARICA 分類精度 */}
      {tab === 0 && metrics && (
        <CardBase paddingSize="small" mb={0.5}>
          <div style={{ padding: '8px 12px' }}>
            <SubSectionTitle headlineLevel={3}>INV-1 ARICA 分類精度 (30日)</SubSectionTitle>
            <Note mt={0.5}>arica_receipt_tracking ベース。 partner_id NULL や registered 未到達は学習不能。</Note>
            <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <tbody>
                <Row label="total" value={metrics.arica.total} />
                <Row label="registered (= 登録完了)" value={metrics.arica.registered} highlight="#338650" />
                <Row label="unprocessed (= pending / in_triage)" value={metrics.arica.unprocessed} highlight={metrics.arica.unprocessed > 0 ? '#dd7a00' : undefined} />
                <Row label="null partner_id 警告" value={metrics.arica.null_issue} highlight={metrics.arica.null_issue > 0 ? '#c33939' : undefined} />
                <Row label="登録率" value={formatPct(metrics.arica.registered_rate)} highlight="#338650" />
              </tbody>
            </table>
            {metrics.arica.total > 0 && (
              <div style={{ marginTop: '8px' }}>
                <ProgressBar value={(metrics.arica.registered / metrics.arica.total) * 100} maxValue={100} />
              </div>
            )}
          </div>
        </CardBase>
      )}

      {/* TAB 1: INV-2 Cadence 学習度 */}
      {tab === 1 && metrics && (
        <>
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={3}>INV-2 Partner cadence 学習度</SubSectionTitle>
              <Note mt={0.5}>partner_billing_cadence の confidence 分布 (= issue_date / due_date 自動推定の信頼度)</Note>
              <div style={{ marginTop: '8px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <DistBlock label="高 (≥0.85)" value={metrics.cadence.high} total={metrics.cadence.total} color="#338650" />
                <DistBlock label="中 (0.70–0.85)" value={metrics.cadence.medium} total={metrics.cadence.total} color="#7c5cff" />
                <DistBlock label="低 (<0.70)" value={metrics.cadence.low} total={metrics.cadence.total} color="#dd7a00" />
                <DistBlock label="irregular / unknown" value={metrics.cadence.irregular} total={metrics.cadence.total} color="#8a8580" />
                <DistBlock label="total" value={metrics.cadence.total} total={metrics.cadence.total} color="#2e7cf6" />
              </div>
            </div>
          </CardBase>
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={4}>cadence 詳細 (上位 200 件)</SubSectionTitle>
              <div style={{ marginTop: '8px', overflowX: 'auto', maxHeight: '600px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                      <th style={th}>partner_id</th>
                      <th style={th}>name</th>
                      <th style={th}>issue pattern</th>
                      <th style={th}>due pattern</th>
                      <th style={{ ...th, textAlign: 'right' }}>conf</th>
                      <th style={{ ...th, textAlign: 'right' }}>samples</th>
                      <th style={{ ...th, textAlign: 'right' }}>mode_ratio</th>
                      <th style={th}>last_review</th>
                      <th style={th}>manual?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cadences.slice(0, 200).map((c, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                        <td style={td}>{c.partner_id}</td>
                        <td style={td}>{c.partner_name || '—'}</td>
                        <td style={td}>{c.issue_day_pattern}{c.issue_anchor_day ? ` (${c.issue_anchor_day})` : ''}</td>
                        <td style={td}>{c.due_day_pattern}{c.due_anchor_day ? ` (${c.due_anchor_day})` : ''}</td>
                        <td style={{ ...td, textAlign: 'right', color: c.confidence >= 0.85 ? '#338650' : c.confidence >= 0.70 ? '#7c5cff' : '#dd7a00' }}>
                          {formatPct(c.confidence)}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{c.sample_size}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{c.mode_ratio == null ? '—' : (c.mode_ratio * 100).toFixed(0) + '%'}</td>
                        <td style={{ ...td, fontSize: '0.6875rem' }}>{c.last_reviewed_at?.slice(0, 10) || '—'}</td>
                        <td style={td}>{c.manual_override ? 'yes' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardBase>
        </>
      )}

      {/* TAB 2: INV-3 Curated patterns */}
      {tab === 2 && metrics && (
        <>
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={3}>INV-3 partner-patterns.md 記録率</SubSectionTitle>
              <Note mt={0.5}>人手 curated パターン (= partner_curated_patterns) の coverage と、 deal は多いが未記録の partner</Note>
              <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <tbody>
                  <Row label="markdown 記載 (parser 経由)" value={metrics.curated_patterns.total_partners_in_md} />
                  <Row label="D1 partner_curated_patterns row 数" value={metrics.curated_patterns.total_in_d1} highlight="#338650" />
                  <Row label="partner_patterns_index trusted/reviewed 件" value={metrics.curated_patterns.trusted_in_index} highlight="#7c5cff" />
                  <Row label="partner_patterns_index missing 件" value={metrics.curated_patterns.missing_in_index} highlight={metrics.curated_patterns.missing_in_index > 5 ? '#dd7a00' : undefined} />
                  <Row label="partner_patterns_index 全件" value={metrics.curated_patterns.total_in_index} />
                  <Row label="trusted/total" value={formatPct(curatedRate)} />
                </tbody>
              </table>
            </div>
          </CardBase>
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={4}>
                記録優先候補 (= cache に 5+ deal あるが curated 未記録 / {metrics.curated_patterns.missing_partners_with_high_deals.length} 件)
              </SubSectionTitle>
              <Note mt={0.5}>これらの partner を partner-patterns.md に追加 → seed 再実行で curated 化推奨</Note>
              <div style={{ marginTop: '8px', overflowX: 'auto', maxHeight: '500px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                      <th style={th}>partner_id</th>
                      <th style={th}>partner_name</th>
                      <th style={{ ...th, textAlign: 'right' }}>deal_count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.curated_patterns.missing_partners_with_high_deals.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                        <td style={td}>{p.partner_id}</td>
                        <td style={td}>{p.partner_name || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', color: p.deal_count >= 30 ? '#c33939' : '#dd7a00' }}>
                          {p.deal_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardBase>
        </>
      )}

      {/* TAB 3: INV-4 重複検出 */}
      {tab === 3 && metrics && (
        <CardBase paddingSize="small" mb={0.5}>
          <div style={{ padding: '8px 12px' }}>
            <SubSectionTitle headlineLevel={3}>INV-4 重複検出 (cross_source_dedup)</SubSectionTitle>
            <Note mt={0.5}>ARICA / filebox / manual で同じ請求書が 2 回以上来た時の検出回数 (30日)</Note>
            <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <tbody>
                <Row label="検出 (30日)" value={metrics.duplicate_detection.fired_30d} highlight="#338650" />
                <Row label="ARICA 経由" value={metrics.duplicate_detection.by_source.arica} />
                <Row label="filebox 経由" value={metrics.duplicate_detection.by_source.filebox} />
                <Row label="manual 経由" value={metrics.duplicate_detection.by_source.manual} />
              </tbody>
            </table>
            {metrics.duplicate_detection.fired_30d === 0 && (
              <Note mt={0.5}>30 日 検出ゼロ。 多重投入は発生していない。</Note>
            )}
          </div>
        </CardBase>
      )}

      {/* TAB 4: INV-7 結果ログ + 品質問題 */}
      {tab === 4 && metrics && issues && (
        <>
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={3}>INV-7 結果ログ (P1 invoice_outcome_log, v39)</SubSectionTitle>
              <Note mt={0.5}>各 proposal の最終結果 + Recommend baseline との field 単位 diff (= partner_curated_patterns / cache 経由 baseline)</Note>
              <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <tbody>
                  <Row label="total" value={metrics.outcome_log.total} />
                  <Row label="approved_as_is (= recommend と完全一致のまま登録)" value={metrics.outcome_log.approved_as_is} highlight="#338650" />
                  <Row label="approved_with_modification (= recommend を 1 field 以上修正)" value={metrics.outcome_log.approved_with_modification} highlight="#dd7a00" />
                  <Row label="rejected" value={metrics.outcome_log.rejected} highlight={metrics.outcome_log.rejected > 0 ? '#c33939' : undefined} />
                  <Row label="no_baseline (= recommend 未生成 / 学習データ不足)" value={metrics.outcome_log.no_baseline ?? 0} highlight={(metrics.outcome_log.no_baseline ?? 0) > 0 ? '#8a8580' : undefined} />
                </tbody>
              </table>
            </div>
          </CardBase>

          {/* F9: Recommend 提案率 / 採用率 / 完全一致率 — 3 metric 明示分離 */}
          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={3}>Recommend baseline metrics (= 学習機構 prefill の効きぐあい)</SubSectionTitle>
              <Note mt={0.5}>
                <strong>提案率 = emitted / total</strong>(curated/cache 経由で Recommend が出せた割合)、
                <strong>採用率 = used / emitted</strong>(出された prefill が触られず最終登録された割合)、
                <strong>完全一致率 = match / emitted</strong>(account/section/item/tax_code すべて一致した row の割合)
              </Note>
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                <KpiBlock
                  label="Recommend 提案率"
                  value={formatPct(metrics.outcome_log.prefill_emitted_rate)}
                  sub={`${metrics.outcome_log.prefill_emitted_count ?? 0} / ${metrics.outcome_log.total} (= curated_markdown / cache_majority_vote が prefill を emit した)`}
                  color="#2e7cf6"
                />
                <KpiBlock
                  label="Recommend 採用率"
                  value={formatPct(metrics.outcome_log.prefill_used_rate)}
                  sub={`${metrics.outcome_log.prefill_used_count ?? 0} / ${metrics.outcome_log.prefill_emitted_count ?? 0} (= ユーザーが prefill を触らずそのまま登録した)`}
                  color="#338650"
                />
                <KpiBlock
                  label="完全一致率"
                  value={formatPct(metrics.outcome_log.prefill_match_rate)}
                  sub={`${metrics.outcome_log.prefill_match_count ?? 0} / ${metrics.outcome_log.prefill_emitted_count ?? 0} (= 全 field が recommend と完全一致)`}
                  color="#7c5cff"
                />
              </div>
              <Note mt={0.5}>
                {'* 採用率 vs 完全一致率: 通常はほぼ同値だが、 user が修正後に同一値で確定した場合 採用率 < 完全一致率 になりうる。 差が大きいときは 修正→元に戻る循環が起きている可能性 (= UI / curated baseline の見直し対象)'}
              </Note>
            </div>
          </CardBase>

          <CardBase paddingSize="small" mb={0.5}>
            <div style={{ padding: '8px 12px' }}>
              <SubSectionTitle headlineLevel={3}>品質問題 ({issues.stuck_arica_proposals.length + issues.missing_receipt_links.length + issues.missing_audit_summary.length + issues.low_confidence_cadences.length})</SubSectionTitle>
              <QualityList title={`stuck arica proposals (= 7日以上滞留 / ${issues.stuck_arica_proposals.length})`} data={issues.stuck_arica_proposals} cols={['arica_rowid','partner_id','partner_name','processing_status','last_updated']} />
              <QualityList title={`missing receipt links (= registered だが receipt 未紐付 / ${issues.missing_receipt_links.length})`} data={issues.missing_receipt_links} cols={['ref_number','partner_id','freee_deal_id','created_at']} />
              <QualityList title={`missing audit summary (= /api/deal-proposals proposed 後 audit 不在 / ${issues.missing_audit_summary.length})`} data={issues.missing_audit_summary} cols={['ref_number','partner_id','status']} />
              <QualityList title={`low confidence cadences (= 期日推論 信頼度不足 / ${issues.low_confidence_cadences.length})`} data={issues.low_confidence_cadences} cols={['partner_id','partner_name','confidence','sample_size','issue_day_pattern']} />
            </div>
          </CardBase>
        </>
      )}
    </>
  );
};

const th: React.CSSProperties = { padding: '6px 8px', fontSize: '0.6875rem', color: '#6b6560', fontWeight: 500 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

const KpiBlock: React.FC<{ label: string; value: string; sub?: string; color: string }> = ({ label, value, sub, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 600, color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{sub}</div>}
  </div>
);

const Row: React.FC<{ label: string; value: any; highlight?: string }> = ({ label, value, highlight }) => (
  <tr style={{ borderBottom: '1px solid #f0eeec' }}>
    <td style={{ padding: '6px 8px', color: '#6b6560', fontSize: '0.8125rem' }}>{label}</td>
    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: highlight || '#332f2c' }}>{value}</td>
  </tr>
);

const DistBlock: React.FC<{ label: string; value: number; total: number; color: string }> = ({ label, value, total, color }) => (
  <div style={{ minWidth: '120px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{label}</div>
    <div style={{ fontSize: '1.125rem', fontWeight: 600, color }}>{value}</div>
    {total > 0 && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{((value / total) * 100).toFixed(0)}%</div>}
  </div>
);

const QualityList: React.FC<{ title: string; data: any[]; cols: string[] }> = ({ title, data, cols }) => (
  <div style={{ marginTop: '12px' }}>
    <SubSectionTitle headlineLevel={4}>{title}</SubSectionTitle>
    {data.length === 0 ? (
      <Note>該当なし</Note>
    ) : (
      <div style={{ marginTop: '4px', overflowX: 'auto', maxHeight: '300px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
              {cols.map((c, i) => <th key={i} style={th}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 30).map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                {cols.map((c, j) => (
                  <td key={j} style={td}>
                    {row[c] != null ? (typeof row[c] === 'number' && c === 'confidence' ? formatPct(row[c]) : String(row[c]).slice(0, 80)) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
